// Catálogo de telas e cálculos de uso exibidos no Painel Admin.
// Os nomes seguem as abas do menu autenticado (AuthenticatedBottomNavigation).

const PLAN_LABELS = {
  sensor_free: 'Gratuito Sensor',
  starter: 'Starter',
  essential: 'Essencial',
  professional: 'Profissional',
  management: 'Gestão',
  enterprise: 'Enterprise',
  premium: 'Premium',
  pro: 'Pro',
};

export const planLabel = planId => PLAN_LABELS[planId || 'starter'] || String(planId);

export const ADMIN_SCREEN_CATALOG = [
  { key: 'dashboard', label: 'Dashboard', group: 'Visão geral', color: '#607d8b', aliases: ['Dashboard'] },
  { key: 'notificacoes-operacionais', label: 'Notificações', group: 'Visão geral', color: '#f06292', adoption: true, aliases: ['Notificações', 'Notificacoes'] },
  { key: 'sensoriamento', label: 'Central de Operações', group: 'Operação', color: '#5e35b1', adoption: true, module: true, aliases: ['Central de Operações', 'Central de Operacoes', 'SensoriamentoRemoto', 'Mapas e NDVI'] },
  { key: 'caderno', label: 'Caderno de Campo', group: 'Operação', color: '#00897b', adoption: true, module: true, aliases: ['Caderno de Campo'] },
  { key: 'propriedades', label: 'Propriedades e Talhões', group: 'Operação', color: '#1976d2', adoption: true, module: true, aliases: ['ListaPropriedades', 'Propriedades e Talhões'] },
  { key: 'alertas', label: 'Alertas', group: 'Operação', color: '#e53935', adoption: true, module: true, aliases: ['Alertas'] },
  { key: 'previsao', label: 'Previsão Climática', group: 'Monitoramento', color: '#43cea2', adoption: true, module: true, aliases: ['PrevisaoClimatica', 'Previsão Climática', 'Previsao'] },
  { key: 'sensores', label: 'Sensores', group: 'Monitoramento', color: '#ff9800', adoption: true, module: true, aliases: ['Sensores'] },
  { key: 'irrigacao', label: 'Irrigação', group: 'Monitoramento', color: '#0284c7', adoption: true, module: true, aliases: ['Irrigacao', 'Irrigação'] },
  { key: 'produtividade', label: 'Desempenho da Propriedade', group: 'Gestão', color: '#9c27b0', adoption: true, module: true, aliases: ['Produtividade', 'Desempenho da Safra'] },
  { key: 'pecuaria', label: 'Pecuária', group: 'Gestão', color: '#8d6e63', adoption: true, module: true, aliases: ['Pecuaria', 'Pecuária'] },
  { key: 'colaboracao', label: 'Colaboração e Equipe', group: 'Gestão', color: '#3949ab', adoption: true, module: true, aliases: ['Colaboração e Equipe', 'Colaboracao'] },
  { key: 'perfil', label: 'Perfil e Conta', group: 'Conta', color: '#78909c', aliases: ['Perfil'] },
  { key: 'planos', label: 'Meus Planos', group: 'Conta', color: '#fbc02d', aliases: ['MeusPlanos', 'Planos'] },
];

// Visitas acima deste limite (aba esquecida aberta) distorcem a média de permanência.
export const MAX_VISIT_SECONDS = 1800;
const MAX_TRANSITION_GAP_MS = 30 * 60 * 1000;
const DAY_MS = 86400000;

const normalizeText = value => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const SCREEN_BY_ALIAS = new Map();
ADMIN_SCREEN_CATALOG.forEach(screen => {
  [screen.key, screen.label, ...screen.aliases].forEach(alias => SCREEN_BY_ALIAS.set(normalizeText(alias), screen));
});

export function resolveScreen(tela) {
  if (!tela) return null;
  const known = SCREEN_BY_ALIAS.get(normalizeText(tela));
  if (known) return known;
  return { key: `outra:${tela}`, label: String(tela), group: 'Outras', color: '#9e9e9e', aliases: [] };
}

export const screenLabel = tela => resolveScreen(tela)?.label || '-';

// Chaves de telas desconhecidas vêm prefixadas com "outra:".
const labelOfKey = key => screenLabel(String(key).replace(/^outra:/, ''));

export const isVisitEvent = ev => !String(ev?.evento || '').endsWith('_leave');

const pad = n => String(n).padStart(2, '0');
const dayKeyOf = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const pct = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);
const average = (sum, count, digits = 1) => (count > 0 ? Number((sum / count).toFixed(digits)) : 0);

export function formatDuration(seconds) {
  const total = Math.round(Number(seconds) || 0);
  if (total <= 0) return '-';
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}min ${pad(total % 60)}s`;
  return `${Math.floor(minutes / 60)}h ${pad(minutes % 60)}min`;
}

export function getSegmentThresholds(periodDays) {
  const days = periodDays || 7;
  return {
    power: Math.max(2, Math.round((10 * days) / 7)),
    regular: Math.max(1, Math.round((3 * days) / 7)),
  };
}

export const RECENCY_BUCKETS = [
  { key: 'hoje', label: 'Hoje', max: 0 },
  { key: '1-3', label: '1 a 3 dias', max: 3 },
  { key: '4-7', label: '4 a 7 dias', max: 7 },
  { key: '8-14', label: '8 a 14 dias', max: 14 },
  { key: '15+', label: '15+ dias', max: Infinity },
];

/**
 * @param {Object} params
 * @param {Array} params.eventos eventos de user_events já filtrados por período e sem admins
 * @param {Array} params.usuarios contas não administradoras
 * @param {number|null} params.periodDays null = todo o período
 */
export function buildUsageAnalytics({ eventos = [], usuarios = [], periodDays = null, now = new Date() }) {
  const nowMs = now.getTime();
  const todayKey = dayKeyOf(now);
  const screens = new Map();
  const people = new Map();
  const dailyMap = new Map();
  const hours = Array(24).fill(0);
  const weekdays = Array(7).fill(0);
  const visits = [];
  let totalSeconds = 0;
  let timedVisits = 0;

  const screenEntry = key => {
    if (!screens.has(key)) {
      screens.set(key, { users: new Set(), visits: 0, userDays: new Map(), seconds: 0, timed: 0, last: 0 });
    }
    return screens.get(key);
  };
  const personEntry = id => {
    if (!people.has(id)) people.set(id, { visits: 0, days: new Set(), screens: new Map(), seconds: 0, last: 0, first: Infinity });
    return people.get(id);
  };

  eventos.forEach(ev => {
    if (!ev.user_id || !ev.tela) return;
    const screen = resolveScreen(ev.tela);
    const ts = new Date(ev.data_hora).getTime();
    if (Number.isNaN(ts)) return;
    const seconds = Math.min(Math.max(Number(ev.duracao_segundos) || 0, 0), MAX_VISIT_SECONDS);

    const sEntry = screenEntry(screen.key);
    const pEntry = personEntry(ev.user_id);
    if (seconds > 0) {
      sEntry.seconds += seconds;
      sEntry.timed += 1;
      pEntry.seconds += seconds;
      totalSeconds += seconds;
      timedVisits += 1;
    }
    if (!isVisitEvent(ev)) return;

    const date = new Date(ts);
    const dayKey = dayKeyOf(date);
    sEntry.users.add(ev.user_id);
    sEntry.visits += 1;
    sEntry.last = Math.max(sEntry.last, ts);
    if (!sEntry.userDays.has(ev.user_id)) sEntry.userDays.set(ev.user_id, new Set());
    sEntry.userDays.get(ev.user_id).add(dayKey);

    pEntry.visits += 1;
    pEntry.days.add(dayKey);
    pEntry.screens.set(screen.key, (pEntry.screens.get(screen.key) || 0) + 1);
    pEntry.last = Math.max(pEntry.last, ts);
    pEntry.first = Math.min(pEntry.first, ts);

    if (!dailyMap.has(dayKey)) dailyMap.set(dayKey, { users: new Set(), visits: 0 });
    dailyMap.get(dayKey).users.add(ev.user_id);
    dailyMap.get(dayKey).visits += 1;
    hours[date.getHours()] += 1;
    weekdays[date.getDay()] += 1;
    visits.push({ userId: ev.user_id, screen: screen.key, ts });
  });

  const activeIds = [...people.entries()].filter(([, p]) => p.visits > 0).map(([id]) => id);
  const activeCount = activeIds.length;
  const moduleKeys = new Set(ADMIN_SCREEN_CATALOG.filter(s => s.module).map(s => s.key));
  const modulesUsedBy = id => [...people.get(id).screens.keys()].filter(key => moduleKeys.has(key)).length;

  const activeSince = days => {
    const limit = nowMs - days * DAY_MS;
    return activeIds.filter(id => people.get(id).last >= limit).length;
  };
  const activeToday = activeIds.filter(id => people.get(id).days.has(todayKey)).length;

  const totalVisits = visits.length;
  const registered = usuarios.length;

  const newLimit = periodDays ? nowMs - periodDays * DAY_MS : 0;
  const newUsers = usuarios.filter(u => u.criado_em && new Date(u.criado_em).getTime() >= newLimit);
  const newActivated = newUsers.filter(u => people.has(u.id) && modulesUsedBy(u.id) > 0).length;

  const kpis = {
    registered,
    active: activeCount,
    activeRate: pct(activeCount, registered),
    dormant: Math.max(registered - activeCount, 0),
    activeToday,
    active7: activeSince(7),
    active30: periodDays === null || periodDays >= 30 ? activeSince(30) : null,
    visits: totalVisits,
    visitsPerUser: average(totalVisits, activeCount),
    activeDaysPerUser: average(activeIds.reduce((sum, id) => sum + people.get(id).days.size, 0), activeCount),
    modulesPerUser: average(activeIds.reduce((sum, id) => sum + modulesUsedBy(id), 0), activeCount),
    avgVisitSeconds: timedVisits > 0 ? Math.round(totalSeconds / timedVisits) : 0,
    totalSeconds,
    newUsers: newUsers.length,
    newActivated,
  };

  const screenRows = [...screens.entries()]
    .map(([key, s]) => {
      const info = resolveScreen(key.replace(/^outra:/, ''));
      const recurring = [...s.userDays.values()].filter(days => days.size >= 2).length;
      return {
        key,
        label: info.label,
        group: info.group,
        color: info.color,
        adoption: Boolean(info.adoption),
        users: s.users.size,
        adoptionRate: pct(s.users.size, activeCount),
        visits: s.visits,
        visitsPerUser: average(s.visits, s.users.size),
        recurringUsers: recurring,
        recurringRate: pct(recurring, s.users.size),
        avgSeconds: s.timed > 0 ? Math.round(s.seconds / s.timed) : 0,
        totalSeconds: s.seconds,
        last: s.last || null,
      };
    })
    .filter(row => row.visits > 0 || row.totalSeconds > 0);

  // Telas de adoção sem nenhum uso também aparecem, para evidenciar módulos esquecidos.
  const presentKeys = new Set(screenRows.map(row => row.key));
  ADMIN_SCREEN_CATALOG.filter(s => s.adoption && !presentKeys.has(s.key)).forEach(s => {
    screenRows.push({
      key: s.key, label: s.label, group: s.group, color: s.color, adoption: true,
      users: 0, adoptionRate: 0, visits: 0, visitsPerUser: 0, recurringUsers: 0, recurringRate: 0,
      avgSeconds: 0, totalSeconds: 0, last: null,
    });
  });
  screenRows.sort((a, b) => b.users - a.users || b.visits - a.visits || a.label.localeCompare(b.label));

  const funnel = [
    { key: 'registered', label: 'Contas cadastradas', value: registered },
    { key: 'accessed', label: 'Acessaram no período', value: activeCount },
    { key: 'module', label: 'Usaram algum módulo', value: activeIds.filter(id => modulesUsedBy(id) >= 1).length },
    { key: 'multi', label: 'Usaram 3+ módulos', value: activeIds.filter(id => modulesUsedBy(id) >= 3).length },
    { key: 'recurring', label: 'Voltaram em 3+ dias', value: activeIds.filter(id => people.get(id).days.size >= 3).length },
  ].map(step => ({ ...step, rate: pct(step.value, registered) }));

  const transitionMap = new Map();
  const byUser = new Map();
  visits.forEach(v => {
    if (!byUser.has(v.userId)) byUser.set(v.userId, []);
    byUser.get(v.userId).push(v);
  });
  byUser.forEach(list => {
    list.sort((a, b) => a.ts - b.ts);
    for (let i = 1; i < list.length; i += 1) {
      const from = list[i - 1];
      const to = list[i];
      if (from.screen === to.screen || to.ts - from.ts > MAX_TRANSITION_GAP_MS) continue;
      const key = `${from.screen}>${to.screen}`;
      transitionMap.set(key, (transitionMap.get(key) || 0) + 1);
    }
  });
  const transitions = [...transitionMap.entries()]
    .map(([key, count]) => {
      const [from, to] = key.split('>');
      return { from: labelOfKey(from), to: labelOfKey(to), count };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  const thresholds = getSegmentThresholds(periodDays);
  const userRows = usuarios.map(u => {
    const p = people.get(u.id);
    const top = p ? [...p.screens.entries()].sort((a, b) => b[1] - a[1])[0] : null;
    const visitCount = p?.visits || 0;
    const daysSince = p?.last ? Math.floor((nowMs - p.last) / DAY_MS) : null;
    let segment = 'sem-acesso';
    if (visitCount >= thresholds.power) segment = 'power';
    else if (visitCount >= thresholds.regular) segment = 'regular';
    else if (visitCount > 0) segment = 'casual';
    return {
      id: u.id,
      nome: u.nome,
      email: u.email,
      plano: u.plano || 'starter',
      trial: Boolean(u.plano_trial),
      planoExpiracao: u.plano_data_expiracao || null,
      criadoEm: u.criado_em || null,
      visits: visitCount,
      activeDays: p?.days.size || 0,
      modules: p ? modulesUsedBy(u.id) : 0,
      topScreen: top ? labelOfKey(top[0]) : '-',
      seconds: p?.seconds || 0,
      last: p?.last || null,
      daysSince,
      segment,
    };
  }).sort((a, b) => b.visits - a.visits || (b.last || 0) - (a.last || 0));

  const segments = {
    power: userRows.filter(r => r.segment === 'power').length,
    regular: userRows.filter(r => r.segment === 'regular').length,
    casual: userRows.filter(r => r.segment === 'casual').length,
    semAcesso: userRows.filter(r => r.segment === 'sem-acesso').length,
    thresholds,
  };

  const recency = RECENCY_BUCKETS.map(bucket => ({ ...bucket, value: 0 }));
  userRows.filter(r => r.daysSince !== null).forEach(r => {
    const bucket = recency.find(b => r.daysSince <= b.max);
    if (bucket) bucket.value += 1;
  });
  recency.push({ key: 'nunca', label: 'Sem acesso no período', max: Infinity, value: segments.semAcesso });

  const planMap = new Map();
  userRows.forEach(r => {
    const key = r.trial ? `${r.plano} (teste)` : r.plano;
    if (!planMap.has(key)) planMap.set(key, { plan: key, total: 0, active: 0, visits: 0 });
    const entry = planMap.get(key);
    entry.total += 1;
    if (r.visits > 0) entry.active += 1;
    entry.visits += r.visits;
  });
  const plans = [...planMap.values()]
    .map(entry => ({ ...entry, rate: pct(entry.active, entry.total), visitsPerActive: average(entry.visits, entry.active) }))
    .sort((a, b) => b.total - a.total);

  const atRisk = userRows
    .filter(r => r.plano !== 'sensor_free' && r.planoExpiracao && new Date(r.planoExpiracao).getTime() > nowMs)
    .filter(r => r.daysSince === null || r.daysSince >= 7)
    .sort((a, b) => (b.daysSince ?? Infinity) - (a.daysSince ?? Infinity))
    .slice(0, 10);

  const spanDays = periodDays || Math.min(90, Math.max(7, Math.ceil((nowMs - Math.min(...[...people.values()].map(p => p.first).filter(Number.isFinite), nowMs)) / DAY_MS) + 1));
  const daily = [];
  for (let i = spanDays - 1; i >= 0; i -= 1) {
    const day = new Date(now);
    day.setDate(day.getDate() - i);
    const entry = dailyMap.get(dayKeyOf(day));
    daily.push({ label: `${day.getDate()}/${day.getMonth() + 1}`, users: entry?.users.size || 0, visits: entry?.visits || 0 });
  }

  return { kpis, screens: screenRows, funnel, transitions, userRows, segments, recency, plans, atRisk, daily, hours, weekdays };
}
